import { memo, useMemo, useState } from 'react'
import { useSession, useSettings } from '../context'
import { AlertCircle, AlertTriangle, Info, Bug, ChevronUp, ChevronDown, Wifi, WifiOff, Clock, Hash, Zap } from 'lucide-react'

export default memo(function StatusBar() {
  const { logs, currentSessionId, isConnected, streamMode, lastUpdate } = useSession()
  const { settings } = useSettings()
  const [expanded, setExpanded] = useState(false)

  const formattedTime = useMemo(() => {
    if (!lastUpdate) return 'Never'
    return lastUpdate.toLocaleTimeString('en-US', {
      hour12: settings.timeFormat === '12hr',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }, [lastUpdate, settings.timeFormat])

  const levelCounts = useMemo(() => {
    const counts = { ERROR: 0, WARN: 0, INFO: 0, DEBUG: 0, OTHER: 0 }
    logs.forEach(log => {
      const level = log.level?.toUpperCase()
      if (counts.hasOwnProperty(level)) counts[level]++
      else counts.OTHER++
    })
    return counts
  }, [logs])

  const connectionLabel = useMemo(() => {
    if (!isConnected) return 'Offline'
    if (streamMode === 'websocket') return 'Live (WS)'
    if (streamMode === 'sse') return 'Live (SSE)'
    return 'Polling (1s)'
  }, [isConnected, streamMode])

  return (
    <footer 
      className={`flex-shrink-0 border-t text-[10px] gap-2 safe-bottom ${
        expanded ? 'pb-1' : ''
      }`}
      style={{ 
        backgroundColor: 'var(--bg-secondary)', 
        borderColor: 'var(--color-border)',
        color: 'var(--text-muted)'
      }}
    >
      {/* Mobile toggle button */}
      <div className="md:hidden flex justify-center py-0.5">
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-0.5 rounded hover:bg-[var(--color-hover)] transition-colors"
          style={{ color: 'var(--text-secondary)' }}
          aria-label={expanded ? 'Collapse status' : 'Expand status'}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
      </div>

      {/* Main status bar */}
      <div className="flex items-center justify-between px-3 py-1.5">
        {/* Left: Connection + Session */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Connection indicator */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="relative">
              <div 
                className={`w-2 h-2 rounded-full flex-shrink-0 ${isConnected ? 'animate-pulse' : ''}`}
                style={{ backgroundColor: isConnected ? 'var(--color-success)' : 'var(--color-error)' }}
              />
              {isConnected && (
                <div className="absolute inset-0 animate-ping opacity-30">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
                </div>
              )}
            </div>
            <span className="hidden sm:inline font-medium" style={{ color: isConnected ? 'var(--color-success)' : 'var(--color-error)' }}>
              {connectionLabel}
            </span>
            <div className="sm:hidden">
              {isConnected ? <Wifi size={10} /> : <WifiOff size={10} />}
            </div>
          </div>

          {/* Session ID - only show on desktop or when expanded */}
          {currentSessionId && (
            <div className={`items-center gap-1 min-w-0 ${expanded ? 'flex' : 'hidden sm:flex'}`}>
              <span className="opacity-60">Session:</span>
              <span 
                className="font-mono truncate max-w-[80px] sm:max-w-[120px] md:max-w-[180px]"
                style={{ color: 'var(--text-secondary)' }}
                title={currentSessionId}
              >
                {currentSessionId}
              </span>
            </div>
          )}
        </div>

        {/* Center: Level counts - always show on mobile when expanded */}
        <div className={`${expanded ? 'flex' : 'hidden'} md:flex items-center gap-2`}>
          <div className="flex items-center gap-1">
            <Hash size={10} className="opacity-60" />
            <span style={{ color: 'var(--text-secondary)' }}>{logs.length.toLocaleString()}</span>
          </div>
          
          {levelCounts.ERROR > 0 && (
            <LevelCount level="ERROR" count={levelCounts.ERROR} color="var(--color-error)" />
          )}
          {levelCounts.WARN > 0 && (
            <LevelCount level="WARN" count={levelCounts.WARN} color="var(--color-warn)" />
          )}
          {levelCounts.INFO > 0 && (
            <LevelCount level="INFO" count={levelCounts.INFO} color="var(--color-info)" />
          )}
          {levelCounts.DEBUG > 0 && (
            <LevelCount level="DEBUG" count={levelCounts.DEBUG} color="var(--color-debug)" />
          )}
          {levelCounts.OTHER > 0 && (
            <LevelCount level="OTHER" count={levelCounts.OTHER} color="var(--text-muted)" />
          )}
        </div>

        {/* Right: Last update */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`${expanded ? 'flex' : 'hidden sm:flex'} items-center gap-1`}>
            <Clock size={10} className="opacity-60" />
            <span style={{ color: 'var(--text-secondary)' }}>{formattedTime}</span>
          </div>
        </div>
      </div>

      {/* Expanded mobile view */}
      {expanded && (
        <div className="md:hidden px-3 py-1.5 border-t border-[var(--color-border)] space-y-2 animate-slideDown">
          {/* Detailed stats */}
          <div className="grid grid-cols-2 gap-3">
            {/* Session details */}
            {currentSessionId && (
              <div className="space-y-1">
                <div className="text-[9px] uppercase tracking-wider opacity-60">Session ID</div>
                <div 
                  className="font-mono text-[10px] truncate"
                  style={{ color: 'var(--text-secondary)' }}
                  title={currentSessionId}
                >
                  {currentSessionId}
                </div>
              </div>
            )}

            {/* Update time details */}
            <div className="space-y-1">
              <div className="text-[9px] uppercase tracking-wider opacity-60">Last Updated</div>
              <div className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                {formattedTime}
              </div>
            </div>

            {/* Total logs */}
            <div className="space-y-1">
              <div className="text-[9px] uppercase tracking-wider opacity-60">Total Logs</div>
              <div className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                {logs.length.toLocaleString()}
              </div>
            </div>

            {/* Connection status */}
            <div className="space-y-1">
              <div className="text-[9px] uppercase tracking-wider opacity-60">Status</div>
              <div className="text-[10px] flex items-center gap-1" style={{ 
                color: isConnected ? 'var(--color-success)' : 'var(--color-error)' 
              }}>
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'animate-pulse' : ''}`} 
                     style={{ backgroundColor: isConnected ? 'var(--color-success)' : 'var(--color-error)' }} />
                {connectionLabel}
              </div>
            </div>
          </div>

          {/* Detailed level breakdown */}
          <div className="space-y-1">
            <div className="text-[9px] uppercase tracking-wider opacity-60 mb-1">Log Breakdown</div>
            <div className="grid grid-cols-3 gap-2">
              <LevelDetail count={levelCounts.ERROR} label="Error" color="var(--color-error)" />
              <LevelDetail count={levelCounts.WARN} label="Warning" color="var(--color-warn)" />
              <LevelDetail count={levelCounts.INFO} label="Info" color="var(--color-info)" />
              <LevelDetail count={levelCounts.DEBUG} label="Debug" color="var(--color-debug)" />
              <LevelDetail count={levelCounts.OTHER} label="Other" color="var(--text-muted)" />
            </div>
          </div>
        </div>
      )}
    </footer>
  )
})

const LevelCount = memo(function LevelCount({ level, count, color }) {
  const icons = {
    ERROR: AlertCircle,
    WARN: AlertTriangle,
    INFO: Info,
    DEBUG: Bug,
    OTHER: Hash
  }
  
  const Icon = icons[level] || Hash
  
  return (
    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)]">
      <Icon size={10} style={{ color }} />
      <span className="font-medium" style={{ color }}>{count}</span>
    </div>
  )
})

const LevelDetail = memo(function LevelDetail({ count, label, color }) {
  return (
    <div className="flex flex-col items-center p-1.5 rounded bg-[var(--bg-tertiary)]">
      <span className="text-xs font-bold" style={{ color }}>{count}</span>
      <span className="text-[9px] opacity-75 mt-0.5">{label}</span>
    </div>
  )
})